/**
 * Deterministic plain-English → EventBridge schedule parser.
 *
 * Shared by the home-page hero (deriveDraftFromDescription) and the mock
 * co-builder (coBuilderLocalParse) so both turn "every Monday at 8am" into the
 * same concrete expression instead of silently falling back to the template's
 * rate(1 day). The real studio-api co-builder honors the same contract.
 *
 * EventBridge cron() is ALWAYS interpreted in UTC — there is no agent-local
 * timezone — so any time the creator names is taken as UTC and labels that
 * surface a parsed schedule must say so ("confirm your schedule (UTC)").
 *
 * `rate()` floor is one hour (the renderer's async sub-mode); anything faster
 * is clamped up to rate(1 hour).
 */

/** Day-of-week → EventBridge cron token (Sun-based, but the token is the
 *  3-letter abbreviation EventBridge accepts). */
const DOW: Record<string, string> = {
  sunday: 'SUN', monday: 'MON', tuesday: 'TUE', wednesday: 'WED',
  thursday: 'THU', friday: 'FRI', saturday: 'SAT',
};

export interface ParsedSchedule {
  /** EventBridge rate()/cron() expression. */
  expression: string;
  /** True when a concrete cadence was parsed from the text; false when we fell
   *  back to the daily default and the builder should ask the creator to
   *  confirm. */
  parsed: boolean;
  /** Human-readable cadence for a "confirm your schedule (UTC)" label. Always
   *  UTC — EventBridge cron has no timezone. */
  label: string;
}

/** The default when nothing parses — once a day, the renderer's safe floor. */
const DEFAULT: ParsedSchedule = {
  expression: 'rate(1 day)',
  parsed: false,
  label: 'once a day',
};

/** Pull an hour-of-day (UTC) out of phrases like "8am", "08:00", "at 7",
 *  "5 pm", "noon", "midnight". Returns 0-23 or null. */
function parseHour(text: string): number | null {
  const lower = text.toLowerCase();
  if (/\bnoon\b/.test(lower)) return 12;
  if (/\bmidnight\b/.test(lower)) return 0;
  // "8am", "8 am", "8:30am", "08:00", "at 8", "by 7pm"
  const m = lower.match(/\b(?:at\s+|by\s+)?([01]?\d|2[0-3])(?::([0-5]\d))?\s*(am|pm)?\b/);
  if (!m) return null;
  // Reject a bare number that isn't anchored to a time word — "every 3 days"
  // must not be read as 3am. Require either am/pm, a colon, or an at/by lead.
  const hasTimeCue = !!m[3] || m[2] !== undefined || /\b(?:at|by)\s+\d/.test(lower);
  if (!hasTimeCue) return null;
  let hour = parseInt(m[1], 10);
  const ampm = m[3];
  if (ampm === 'pm' && hour < 12) hour += 12;
  if (ampm === 'am' && hour === 12) hour = 0;
  if (hour > 23) return null;
  return hour;
}

function timeLabel(hour: number): string {
  return `${String(hour).padStart(2, '0')}:00 UTC`;
}

/**
 * Parse a cadence out of free text. Recognizes:
 *   - "every N minutes/hours/days/weeks" → rate(...)
 *   - "every hour" / "hourly" → rate(1 hour)
 *   - "every day" / "daily" / "every morning" (+ optional time) → cron daily
 *   - "every weekday" / "weekdays" (+ time) → cron Mon-Fri
 *   - "every Monday" / "weekly" (+ time) → cron weekly (defaults to Monday)
 *   - "every month" / "monthly" (+ time) → cron 1st of month
 * Falls back to rate(1 day) (parsed:false) when nothing matches.
 */
export function parseScheduleExpression(text: string): ParsedSchedule {
  const lower = (text || '').toLowerCase();
  if (!lower.trim()) return DEFAULT;

  const hour = parseHour(lower);
  const at = hour === null ? 0 : hour;
  const atLabel = hour === null ? '08:00 UTC' : timeLabel(at);
  // When no time is named we still emit a concrete cron at 08:00 UTC for daily/
  // weekly/weekday/monthly cadences — a sensible morning default the creator
  // confirms — but the cron uses the named hour when present.
  const cronHour = hour === null ? 8 : at;
  const cronLabel = `${timeLabel(cronHour)}`;

  // "every N <unit>" → rate(). EventBridge rate floor is 1 hour for our async
  // sub-mode; sub-hour cadences clamp up.
  const everyN = lower.match(/\bevery\s+(\d+)\s+(minute|hour|day|week)s?\b/);
  if (everyN) {
    const n = parseInt(everyN[1], 10);
    const unit = everyN[2];
    if (unit === 'minute' || (unit === 'hour' && n < 1)) {
      return { expression: 'rate(1 hour)', parsed: true, label: 'every hour (clamped from a sub-hour cadence)' };
    }
    const plural = n === 1 ? unit : `${unit}s`;
    return { expression: `rate(${n} ${plural})`, parsed: true, label: `every ${n} ${plural}` };
  }

  // Named day of the week → weekly cron on that day.
  for (const [day, token] of Object.entries(DOW)) {
    const re = new RegExp(`\\b(?:every|each|on)\\s+${day}s?\\b`);
    if (re.test(lower)) {
      return {
        expression: `cron(0 ${cronHour} ? * ${token} *)`,
        parsed: true,
        label: `every ${day.charAt(0).toUpperCase() + day.slice(1)} at ${cronLabel}`,
      };
    }
  }

  // Weekdays → Mon-Fri.
  if (/\b(every\s+weekday|weekdays?|each\s+weekday|business\s+days?)\b/.test(lower)) {
    return {
      expression: `cron(0 ${cronHour} ? * MON-FRI *)`,
      parsed: true,
      label: `every weekday at ${cronLabel}`,
    };
  }

  // Monthly → 1st of the month.
  if (/\b(monthly|every\s+month|each\s+month)\b/.test(lower)) {
    return {
      expression: `cron(0 ${cronHour} 1 * ? *)`,
      parsed: true,
      label: `the 1st of each month at ${cronLabel}`,
    };
  }

  // Weekly (no specific day) → defaults to Monday.
  if (/\b(weekly|every\s+week|each\s+week)\b/.test(lower)) {
    return {
      expression: `cron(0 ${cronHour} ? * MON *)`,
      parsed: true,
      label: `every Monday at ${cronLabel}`,
    };
  }

  // Hourly.
  if (/\b(hourly|every\s+hour|each\s+hour)\b/.test(lower)) {
    return { expression: 'rate(1 hour)', parsed: true, label: 'every hour' };
  }

  // Daily / "every morning|evening|night|day" (with an optional named time).
  if (/\b(daily|nightly|every\s+(?:day|morning|evening|night)|each\s+(?:day|morning|evening|night))\b/.test(lower)) {
    if (hour !== null) {
      return { expression: `cron(0 ${at} ? * * *)`, parsed: true, label: `every day at ${atLabel}` };
    }
    // "every morning" implies ~08:00 UTC; "every night/evening" ~20:00 UTC.
    const impliedHour = /\b(night|evening)\b/.test(lower) ? 20 : 8;
    return {
      expression: `cron(0 ${impliedHour} ? * * *)`,
      parsed: true,
      label: `every day at ${timeLabel(impliedHour)}`,
    };
  }

  return DEFAULT;
}

/**
 * Client-side validation for a Microsoft Teams Incoming-Webhook URL. We only
 * assert the shape the connector publishes — https + a *.webhook.office.com
 * host with a path — never that it's reachable (that's the server's job at
 * delivery time). Empty is treated as "not yet entered", not invalid, so the
 * field error only fires once the creator has typed something.
 */
export function isValidTeamsWebhook(url: string | undefined | null): boolean {
  const u = (url || '').trim();
  if (!u) return true; // empty = "not set", handled by the required check
  return /^https:\/\/[a-z0-9.\-]*\.webhook\.office\.com\/.+/i.test(u);
}

/** Full day-of-week names for the reverse (expression → label) direction. */
const DOW_NAME: Record<string, string> = {
  SUN: 'Sunday', MON: 'Monday', TUE: 'Tuesday', WED: 'Wednesday',
  THU: 'Thursday', FRI: 'Friday', SAT: 'Saturday',
};

function pluralUnit(n: number, unit: string): string {
  return n === 1 ? unit : `${unit}s`;
}

/**
 * Reverse of parseScheduleExpression: turn a concrete EventBridge expression
 * back into a friendly, UTC-labelled cadence ("Every weekday at 08:00 UTC").
 * Used by the builder's "Confirm your schedule (UTC)" affordance and the
 * automation operate surface so a raw cron string is never shown to creators.
 * Unknown/unparseable expressions fall back to the verbatim expression so we
 * never hide what will actually be scheduled.
 */
export function describeScheduleExpression(expr: string | undefined | null): string {
  const e = (expr || '').trim();
  if (!e) return 'No schedule set';

  // rate(N unit)
  const rate = e.match(/^rate\(\s*(\d+)\s+(minute|hour|day|week)s?\s*\)$/i);
  if (rate) {
    const n = parseInt(rate[1], 10);
    const unit = rate[2].toLowerCase();
    if (n === 1) return `Every ${unit}`;
    return `Every ${n} ${pluralUnit(n, unit)}`;
  }

  // cron(min hour day-of-month month day-of-week year)
  const cron = e.match(/^cron\(\s*(\S+)\s+(\S+)\s+(\S+)\s+(\S+)\s+(\S+)\s+(\S+)\s*\)$/i);
  if (cron) {
    const [, min, hr, dom, , dow] = cron;
    const m = parseInt(min, 10);
    const h = parseInt(hr, 10);
    const at = Number.isFinite(h) && Number.isFinite(m)
      ? `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')} UTC`
      : null;
    const atSuffix = at ? ` at ${at}` : '';

    // Monthly: a numeric day-of-month with a wildcard day-of-week.
    if (/^\d+$/.test(dom) && (dow === '?' || dow === '*')) {
      const d = parseInt(dom, 10);
      const ord = d === 1 ? '1st' : d === 2 ? '2nd' : d === 3 ? '3rd' : `${d}th`;
      return `The ${ord} of each month${atSuffix}`;
    }

    const dowUpper = dow.toUpperCase();
    if (dowUpper === 'MON-FRI') return `Every weekday${atSuffix}`;
    if (DOW_NAME[dowUpper]) return `Every ${DOW_NAME[dowUpper]}${atSuffix}`;

    // Daily: wildcard day-of-week and day-of-month.
    if ((dow === '?' || dow === '*') && (dom === '*' || dom === '?')) {
      return `Every day${atSuffix}`;
    }
    return at ? `Daily${atSuffix}` : e;
  }

  return e; // unknown shape — show it verbatim rather than guess
}
